import { Divider, Flex, Stack, Image, VStack } from "@chakra-ui/react";
import {
  FiHome,
  FiSettings,
  FiUsers,
  FiAtSign,
  FiLayers,
  FiLayout,
  FiMail,
  FiDollarSign,
  FiBarChart2,
} from "react-icons/fi";
import { NavButton } from "./NavButton";
import { UserProfile } from "./UserProfile";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";
import { KYDScope } from "@common/interfaces/ICognito";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import EmailSmsMarketingCard from "src/Common/EmailSmsMarketingCard";

function isActive(path: string, pathname: string, exact = true) {
  return Boolean(
    matchPath({ path, caseSensitive: false, end: exact }, pathname)
  );
}

const sidebarItems = [
  {
    label: "Overview",
    route: "/dashboard",
    icon: FiHome,
    condition: (scopes: KYDScope[]) =>
      scopes.includes("organizations/overview:read") ||
      scopes.includes("organizations/write"),
  },
  {
    label: "Events",
    route: "/events",
    path: "/events/*",
    icon: FiLayout,
  },
  {
    label: "Bundles",
    route: "/bundles",
    path: "/bundles/*",
    icon: FiLayers,
  },
  {
    label: "Engage",
    route: "/campaigns",
    path: "/engage/*",
    icon: FiMail,
    condition: (scopes: KYDScope[]) =>
      [
        "crm/read",
        "crm/write",
        "crm/campaigns:read",
        "crm/campaigns:write",
        "crm/segments:read",
        "crm/segments:write",
        "crm/contacts:read",
        "crm/contacts:write",
        "crm/imports:read",
        "crm/imports:write",
      ].some((perm) => scopes.includes(perm as KYDScope)),
    subItems: [
      {
        label: "Emails",
        route: "/campaigns",
        path: "/campaigns/*",
        condition: (scopes: KYDScope[]) => true,
      },
      {
        label: "Ads",
        route: "/campaigns/ads",
        path: "/campaigns/ads/*",
        condition: (scopes: KYDScope[]) => true,
      },
      {
        label: "Automations",
        route: "/campaigns/automations",
        path: "/campaigns/automations/*",
        condition: (scopes: KYDScope[]) =>
          scopes.includes("crm/read") || scopes.includes("crm/write"),
      },
      {
        label: "Segments",
        route: "/campaigns/segments",
        path: "/campaigns/segments/*",
        condition: (scopes: KYDScope[]) =>
          scopes.includes("crm/segments:read") ||
          scopes.includes("crm/segments:write") ||
          scopes.includes("crm/read") ||
          scopes.includes("crm/write"),
      },
      {
        label: "Contacts",
        route: "/campaigns/contacts",
        path: "/campaigns/contacts/*",
        condition: (scopes: KYDScope[]) =>
          scopes.includes("crm/contacts:read") ||
          scopes.includes("crm/contacts:write") ||
          scopes.includes("crm/read") ||
          scopes.includes("crm/write"),
      },
      {
        label: "Imports",
        route: "/campaigns/imports",
        path: "/campaigns/imports/*",
        condition: (scopes: KYDScope[]) =>
          scopes.includes("crm/imports:read") ||
          scopes.includes("crm/imports:write") ||
          scopes.includes("crm/read") ||
          scopes.includes("crm/write"),
      },
      {
        label: "Settings",
        route: "/campaigns/settings",
        path: "/campaigns/settings/*",
        condition: (scopes: KYDScope[]) => scopes.includes("crm/write"),
      },
    ],
  },
  {
    label: "Team",
    route: "/team",
    icon: FiUsers,
    condition: (scopes: KYDScope[]) =>
      scopes.includes("organizations/write") ||
      scopes.includes("organizations/users:read") ||
      scopes.includes("organizations/users:write"),
  },
  {
    label: "Bio",
    route: "/bio",
    icon: FiAtSign,
  },
  {
    label: "Payouts",
    route: "/payouts",
    icon: FiDollarSign,
    condition: (scopes: KYDScope[]) =>
      scopes.includes("organizations/write") ||
      scopes.includes("organizations/payouts:write") ||
      scopes.includes("organizations/payouts:read"),
  },
  {
    label: "Reporting",
    route: "/reporting",
    icon: FiBarChart2,
    condition: (scopes: KYDScope[]) =>
      scopes.includes("organizations/payouts:read") ||
      scopes.includes("organizations/read"),
  },
  {
    label: "Settings",
    route: "/settings/organization",
    icon: FiSettings,
    condition: (scopes: KYDScope[]) => scopes.includes("organizations/write"),
    subItems: [
      {
        label: "Organization",
        route: "/settings/organization",
        path: "/settings/organization/*",
      },
      {
        label: "Billing",
        route: "/settings/billing",
        path: "/settings/billing/*",
        condition: (scopes: KYDScope[]) =>
          scopes.includes("organizations/write"),
      },
    ],
  },
];

export const Sidebar = ({
  currentUser,
  onOrganizationClick,
  scopes = [],
}: {
  currentUser: KYDOrganizationUser | null;
  onOrganizationClick: () => void;
  scopes: KYDScope[];
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  return (
    <Flex as="section" minH="100vh" zIndex="999">
      <Flex
        flex="1"
        bg="bg-surface"
        overflowY="auto"
        maxW={{ base: "full", sm: "250px" }}
        py={{ base: "2", sm: "4" }}
        px={{ base: "4", sm: "6" }}
        pb={{ base: "4", sm: "8" }}
      >
        <Stack justify="space-between" spacing="1" h="100%">
          <Stack spacing={{ base: "5", sm: "6" }} shouldWrapChildren h="100%">
            <VStack>
              <Image
                maxW="50%"
                src="https://content.kydlabs.com/system/logoblack.png"
              />
              <Divider />
            </VStack>
            <Stack spacing="1">
              {sidebarItems.map((item) => {
                // Filter subItems by their condition if present
                const filteredSubItems = item.subItems
                  ? item.subItems.filter(
                      (sub) => !sub.condition || sub.condition(scopes)
                    )
                  : undefined;

                // Determine if user has access to the main item
                const hasMainAccess = !item.condition || item.condition(scopes);

                // If no main access but has valid subitem, use first valid subitem's route
                let effectiveRoute = item.route;
                if (
                  !hasMainAccess &&
                  filteredSubItems &&
                  filteredSubItems.length > 0
                ) {
                  effectiveRoute = filteredSubItems[0].route;
                }

                // Selection logic remains the same
                const mainSelected =
                  isActive(item.route, pathname, true) ||
                  Boolean(item.path && isActive(item.path, pathname, true));
                let selectedSub = filteredSubItems
                  ?.slice()
                  .reverse()
                  .find((sub) => isActive(sub.path, pathname, true));
                if (!selectedSub) {
                  selectedSub = filteredSubItems?.find((sub) =>
                    isActive(sub.path, pathname, false)
                  );
                }
                const isSelected = mainSelected || Boolean(selectedSub);

                // Only render if user has access to main or at least one subitem
                if (
                  !hasMainAccess &&
                  (!filteredSubItems || filteredSubItems.length === 0)
                ) {
                  return null;
                }

                return (
                  <NavButton
                    key={item.route}
                    isSelected={isSelected}
                    route={effectiveRoute}
                    label={item.label}
                    icon={item.icon}
                    subItems={filteredSubItems}
                    bg={isSelected ? "black" : undefined}
                    color={isSelected ? "white" : undefined}
                    selectedSubItem={selectedSub}
                  />
                );
              })}
            </Stack>
          </Stack>
          <Stack spacing={{ base: "5", sm: "6" }} pt={[4, 0]} pb={[4, 0]}>
            {/* <EmailSmsMarketingCard
              onEnable={() => {
                navigate("/settings/billing");
              }}
            /> */}
            {currentUser && (
              <UserProfile
                currentUser={currentUser}
                onOrganizationClick={onOrganizationClick}
              />
            )}
          </Stack>
        </Stack>
      </Flex>
    </Flex>
  );
};
